import { Injectable } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';

import { SupabaseService } from '../../../core/services/supabase.service';
import { DomainsRepository } from '../domain/domains.repository';
import { CreateDomainInput, Domain } from '../models/domain.model';
import { DomainColor } from '../models/domain-color.model';

interface DomainRow {
  id: number;
  name: string;
  color: DomainColor;
  show_shortcut: boolean;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

@Injectable()
export class SupabaseDomainsRepository extends DomainsRepository {
  private readonly client: SupabaseClient;

  constructor(supabase: SupabaseService) {
    super();
    this.client = supabase.client;
  }

  async getDomains(): Promise<Domain[]> {
    const { data, error } = await this.client
      .from('domains')
      .select('id,name,color,show_shortcut,archived_at,created_at,updated_at')
      .order('name', { ascending: true });

    if (error) throw error;
    return (data ?? []).map(row => this.mapDomain(row as DomainRow));
  }

  async createDomain(input: CreateDomainInput): Promise<Domain> {
    const { data, error } = await this.client
      .from('domains')
      .insert({ name: input.name, color: input.color })
      .select('id,name,color,show_shortcut,archived_at,created_at,updated_at')
      .single();

    if (error) throw error;
    return this.mapDomain(data as DomainRow);
  }

  async updateDomain(id: number, input: CreateDomainInput): Promise<Domain> {
    const { data, error } = await this.client
      .from('domains')
      .update({ name: input.name, color: input.color })
      .eq('id', id)
      .select('id,name,color,show_shortcut,archived_at,created_at,updated_at')
      .single();

    if (error) throw error;
    return this.mapDomain(data as DomainRow);
  }

  async setDomainArchived(id: number, archived: boolean): Promise<void> {
    const { error } = await this.client
      .from('domains')
      .update({ archived_at: archived ? new Date().toISOString() : null })
      .eq('id', id);

    if (error) throw error;
  }

  async setShortcutVisible(id: number, visible: boolean): Promise<void> {
    const { error } = await this.client
      .from('domains')
      .update({ show_shortcut: visible })
      .eq('id', id);

    if (error) throw error;
  }

  async deleteDomain(id: number): Promise<void> {
    const { error } = await this.client
      .from('domains')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  private mapDomain(row: DomainRow): Domain {
    return {
      id: row.id,
      name: row.name,
      color: row.color,
      showShortcut: row.show_shortcut,
      archivedAt: row.archived_at,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    };
  }
}
